/**
 * 音频监控服务（Web Audio API + MediaRecorder）
 *
 * 通过 getUserMedia 获取麦克风 → 同时驱动：
 *   1. AudioContext/AnalyserNode → 实时波形数据 + 音量级别
 *   2. MediaRecorder           → 录制音频 Blob
 */
export class AudioMonitor {
  private stream: MediaStream | null = null;
  private ctx: AudioContext | null = null;
  /** 公开访问：供外部获取波形数据 */
  analyser: AnalyserNode | null = null;
  private _isRunning = false;

  /** MediaRecorder 录音相关 */
  private recorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  /** 调试用：记录 ondataavailable 触发次数 */
  private dataEventCount = 0;
  /** 共享的 ondataavailable 回调（避免 cancel 后丢失） */
  private dataAvailableHandler: ((e: BlobEvent) => void) | null = null;

  /** 复用的时域数据缓冲区（避免高频 GC） */
  private timeDataBuf: Uint8Array = new Uint8Array(0);
  /** 复用的频域数据缓冲区 */
  private freqDataBuf: Uint8Array = new Uint8Array(0);
  /** VAD 推荐轮询间隔（ms） */
  private _pollIntervalMs = 50;
  /** MediaRecorder chunk 间隔（ms） */
  private _recordingChunkMs = 100;

  get isRunning() {
    return this._isRunning;
  }

  /** 是否正在录制（MediaRecorder 状态） */
  get isRecording() {
    return this.recorder?.state === "recording";
  }

  /** 推荐给 VAD 的轮询间隔（移动端更保守） */
  get pollIntervalMs() {
    return this._pollIntervalMs;
  }

  /** MediaRecorder 分片间隔（移动端更保守） */
  get recordingChunkMs() {
    return this._recordingChunkMs;
  }

  /** 请求麦克风权限并启动音频监控 */
  async start(): Promise<boolean> {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      // ── AudioContext 路径（波形分析）──
      this.ctx = new AudioContext();
      const source = this.ctx.createMediaStreamSource(this.stream);

      // 移动端默认降低采样频率，减少连续识别时 CPU 占用
      const isMobile = this.isLikelyMobile();
      this._pollIntervalMs = isMobile ? 80 : 50;
      this._recordingChunkMs = isMobile ? 160 : 100;

      this.analyser = this.ctx.createAnalyser();
      this.analyser.fftSize = 2048;
      this.analyser.smoothingTimeConstant = 0.85;

      source.connect(this.analyser);
      // 不连接 destination（不播放声音）

      // ── MediaRecorder 路径（录音）──
      // 使用 webm 格式（兼容性最好），优先选 opus 编码
      const mimeType = this.getSupportedMimeType();
      if (mimeType) {
        this.recorder = new MediaRecorder(this.stream, { mimeType });
      } else {
        this.recorder = new MediaRecorder(this.stream);
      }

      this.recordedChunks = [];
      this.dataEventCount = 0;

      // 绑定一次，后续 startRecording 复用（避免 cancel 后丢失）
      this.dataAvailableHandler = (e) => {
        this.dataEventCount++;
        if (e.data && e.data.size > 0) {
          this.recordedChunks.push(e.data);
        } else {
          console.warn(`[AudioMonitor] ondataavailable #${this.dataEventCount}: data 为空或 size=0`);
        }
      };
      this.recorder.ondataavailable = this.dataAvailableHandler;
      this.recorder.onerror = (event: Event) => {
        const error = (event as unknown as { error?: Error }).error;
        console.error(`[AudioRecorder] 错误: ${error?.name} - ${error?.message}`);
      };

      this._isRunning = true;
      return true;
    } catch (e) {
      console.warn("[AudioMonitor] Failed:", e);
      this.stop();
      return false;
    }
  }

  /** 获取浏览器支持的录音 MIME 类型 */
  private getSupportedMimeType(): string {
    const types = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/mp4",
    ];
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) return type;
    }
    return "";
  }

  /** 开始录制音频（在 start() 之后调用） */
  startRecording(): void {
    if (!this.recorder || this.recorder.state !== "inactive") return;
    this.recordedChunks = [];
    this.dataEventCount = 0;

    // 恢复共享回调（cancelRecording 可能用空函数覆盖过）
    if (this.dataAvailableHandler) {
      this.recorder.ondataavailable = this.dataAvailableHandler;
    }
    try {
      this.recorder.start(this._recordingChunkMs);
      console.log(
        `[AudioMonitor] MediaRecorder 开始录制, chunk=${this._recordingChunkMs}ms, poll=${this._pollIntervalMs}ms`,
      );
    } catch (e) {
      console.error("[AudioRecorder] start 失败:", e);
    }
  }

  private isLikelyMobile(): boolean {
    const ua = navigator.userAgent || "";
    const hasTouch = navigator.maxTouchPoints > 1;
    return /Android|iPhone|iPad|iPod|Mobile|HarmonyOS/i.test(ua) || hasTouch;
  }

  /** 停止录制并返回 Blob */
  async stopRecording(): Promise<Blob | null> {
    if (!this.recorder || this.recorder.state !== "recording") {
      console.warn(
        `[AudioMonitor] stopRecording 提前返回: recorder=${!!this.recorder}, state=${this.recorder?.state ?? "无"}, chunks=${this.recordedChunks.length}`,
      );
      return null;
    }

    return new Promise((resolve) => {
      this.recorder!.onstop = () => {
        const mimeType = this.recorder?.mimeType || "audio/webm";
        const blob =
          this.recordedChunks.length > 0
            ? new Blob(this.recordedChunks, { type: mimeType })
            : null;
        this.recordedChunks = [];
        console.log(
          `[AudioMonitor] 录制结束, dataEventCount=${this.dataEventCount}, ` +
          `blob=${blob ? `${blob.size}B ${blob.type}` : "null"}`,
        );
        resolve(blob);
      };
      try { this.recorder!.requestData(); } catch (_) { /* ignore */ }
      this.recorder!.stop();
    });
  }

  /** 取消当前录制（不保留数据） */
  cancelRecording(): void {
    if (!this.recorder || this.recorder.state !== "recording") return;
    try {
      this.recorder.ondataavailable = () => {};
      this.recorder.onstop = () => {};
      this.recorder.stop();
      this.recordedChunks = [];
    } catch {
      /* ignore */
    }
  }

  /** 获取时域波形数据（0-255，128=静音中线） */
  getTimeDomainData(): Uint8Array {
    if (!this.analyser) return new Uint8Array(0);
    const data = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteTimeDomainData(data);
    return data;
  }

  /** 获取频率数据（0-255，频谱） */
  getFrequencyData(): Uint8Array {
    if (!this.analyser) return new Uint8Array(0);
    const data = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteFrequencyData(data);
    return data;
  }

  /** 确保时域缓冲区大小匹配 analyser */
  private ensureTimeBuf(size: number): Uint8Array {
    if (this.timeDataBuf.length !== size) {
      this.timeDataBuf = new Uint8Array(size);
    }
    return this.timeDataBuf;
  }

  /** 确保频域缓冲区大小匹配 analyser */
  private ensureFreqBuf(size: number): Uint8Array {
    if (this.freqDataBuf.length !== size) {
      this.freqDataBuf = new Uint8Array(size);
    }
    return this.freqDataBuf;
  }

  /**
   * 检测原始音频是否有有意义的内容
   * @returns maxDeviation: 采样点偏离静音中线(128)的最大值（0~128）
   *   0=完全静音, 1~2=底噪, >3=有声音
   */
  getMaxDeviation(): number {
    if (!this.analyser) return 0;
    const buf = this.ensureTimeBuf(this.analyser.frequencyBinCount);
    this.analyser.getByteTimeDomainData(buf as Uint8Array<ArrayBuffer>);
    let maxDev = 0;
    for (let i = 0; i < buf.length; i++) {
      const dev = Math.abs(buf[i] - 128);
      if (dev > maxDev) maxDev = dev;
    }
    return maxDev;
  }

  /** 计算当前总音量级别（RMS，0~100） */
  getPowerLevel(): number {
    if (!this.analyser) return 0;
    const buf = this.ensureTimeBuf(this.analyser.frequencyBinCount);
    this.analyser.getByteTimeDomainData(buf as Uint8Array<ArrayBuffer>);

    let sum = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = (buf[i] - 128) / 128;
      sum += v * v;
    }
    const rms = Math.sqrt(sum / buf.length);
    return Math.min(rms * 350, 100);
  }

  /**
   * 计算人声频段音量（80Hz ~ 4000Hz）
   * 过滤掉低频噪声（空调、交通）和高频噪音（键盘、电子设备）
   */
  getVoicePowerLevel(): number {
    if (!this.analyser || !this.ctx) return 0;
    const buf = this.ensureFreqBuf(this.analyser.frequencyBinCount);
    this.analyser.getByteFrequencyData(buf as Uint8Array<ArrayBuffer>);

    const sr = this.ctx.sampleRate;
    const binWidth = sr / this.analyser.fftSize;

    // 人声频率范围：80Hz ~ 4000Hz
    const lowBin = Math.floor(80 / binWidth);
    const highBin = Math.min(Math.ceil(4000 / binWidth), buf.length - 1);

    let sum = 0;
    let count = 0;
    for (let i = lowBin; i <= highBin; i++) {
      const v = buf[i] / 255;
      sum += v * v;
      count++;
    }

    if (count === 0) return 0;
    const rms = Math.sqrt(sum / count);
    return Math.min(rms * 180, 100);
  }

  /** 采样率 */
  get sampleRate(): number {
    return this.ctx?.sampleRate || 16000;
  }

  stop() {
    this._isRunning = false;

    // 停止 MediaRecorder
    if (this.recorder && this.recorder.state !== "inactive") {
      try {
        this.recorder.stop();
      } catch {
        /* ignore */
      }
      this.recorder = null;
    }
    this.recordedChunks = [];

    // 停止音频流
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
    if (this.ctx) {
      try {
        this.ctx.close();
      } catch {
        /* ignore */
      }
      this.ctx = null;
    }
    this.analyser = null;
  }

  destroy() {
    this.stop();
  }
}

// ── 单例 ──
let instance: AudioMonitor | null = null;

export function getAudioMonitor(): AudioMonitor {
  if (!instance) instance = new AudioMonitor();
  return instance;
}
